import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Event as EventIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { eventService, packageService } from '../services/api';
import { Event, Package, EventStatus } from '../types';
import { formatTime } from '../utils/dateUtils';

const Events: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<EventStatus | 'all'>('all');
  const [formData, setFormData] = useState({
    contact_name: '',
    contact_phone: '',
    secondary_phone: '',
    address: '',
    google_maps_url: '',
    event_date: '',
    start_time: '',
    extra_hours: 0,
    package_id: '',
    status: EventStatus.PENDING,
    notes: '',
    
    // Campos de extras
    has_advance_payment: false,
    advance_payment_amount: 0,
    discount_percentage: 0,
    extra_hours_count: 0,
    extra_tables: 0,
    extra_chairs: 0,
    extra_playpens: 0,
    extra_toys: 0,
    extra_services: '',
    extra_services_cost: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventsData, packagesData] = await Promise.all([
        eventService.getAll(), // Ahora getAll() usa calendar_mode por defecto
        packageService.getAll(),
      ]);
      setEvents(eventsData);
      setPackages(packagesData);
    } catch (err) {
      setError('Error al cargar los eventos');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (event?: Event) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        contact_name: event.contact_name,
        contact_phone: event.contact_phone,
        secondary_phone: event.secondary_phone || '',
        address: event.address,
        google_maps_url: event.google_maps_url || '',
        event_date: event.event_date,
        start_time: formatTime(event.start_time), // Formatear la hora correctamente
        extra_hours: event.extra_hours,
        package_id: event.package_id,
        status: event.status,
        notes: event.notes || '',
        
        // Campos de extras (con valores por defecto si no existen)
        has_advance_payment: event.has_advance_payment || false,
        advance_payment_amount: event.advance_payment_amount || 0,
        discount_percentage: event.discount_percentage || 0,
        extra_hours_count: event.extra_hours_count || 0,
        extra_tables: event.extra_tables || 0,
        extra_chairs: event.extra_chairs || 0,
        extra_playpens: event.extra_playpens || 0,
        extra_toys: event.extra_toys || 0,
        extra_services: event.extra_services || '',
        extra_services_cost: event.extra_services_cost || 0,
      });
    } else {
      setEditingEvent(null);
      setFormData({
        contact_name: '',
        contact_phone: '',
        secondary_phone: '',
        address: '',
        google_maps_url: '',
        event_date: '',
        start_time: '',
        extra_hours: 0,
        package_id: '',
        status: EventStatus.PENDING,
        notes: '',
        
        // Campos de extras con valores por defecto
        has_advance_payment: false,
        advance_payment_amount: 0,
        discount_percentage: 0,
        extra_hours_count: 0,
        extra_tables: 0,
        extra_chairs: 0,
        extra_playpens: 0,
        extra_toys: 0,
        extra_services: '',
        extra_services_cost: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
  };

  const handleSave = async () => {
    try {
      if (editingEvent) {
        await eventService.update(editingEvent.id, formData);
      } else {
        await eventService.create(formData);
      }
      
      fetchData();
      handleCloseDialog();
    } catch (err) {
      setError('Error al guardar el evento');
      console.error('Error saving event:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este evento?')) {
      try {
        await eventService.delete(id);
        fetchData();
      } catch (err) {
        setError('Error al eliminar el evento');
        console.error('Error deleting event:', err);
      }
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const getStatusColor = (status: EventStatus) => {
    switch (status) {
      case EventStatus.PENDING: return 'warning';
      case EventStatus.CONFIRMED: return 'info';
      case EventStatus.IN_PROGRESS: return 'secondary';
      case EventStatus.COMPLETED: return 'success';
      case EventStatus.CANCELLED: return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: EventStatus) => {
    switch (status) {
      case EventStatus.PENDING: return 'Pendiente';
      case EventStatus.CONFIRMED: return 'Confirmado';
      case EventStatus.IN_PROGRESS: return 'En Progreso';
      case EventStatus.COMPLETED: return 'Completado';
      case EventStatus.CANCELLED: return 'Cancelado';
      default: return status;
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          event.contact_phone.includes(searchTerm) ||
                          event.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Función para formatear precios con máximo 2 decimales
  const formatPrice = (price: number | string | null | undefined) => {
    // Convertir a número y validar
    const numPrice = typeof price === 'string' ? parseFloat(price) : Number(price);
    
    // Si no es un número válido, retornar 0
    if (isNaN(numPrice) || numPrice === null || numPrice === undefined) {
      return '0';
    }
    
    // Asegurar que el número sea positivo
    const validPrice = Math.max(0, numPrice);
    
    return new Intl.NumberFormat('es-MX', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(validPrice);
  };

  // Función para calcular el precio del paquete con descuento
  const calculatePackagePriceWithDiscount = (packagePrice: number, discountPercentage: number) => {
    const discountAmount = packagePrice * (discountPercentage / 100);
    return packagePrice - discountAmount;
  };

  // Función para obtener el precio total del evento (paquete con descuento + extras)
  const calculateTotalEventPrice = (packagePrice: number, discountPercentage: number, extrasCost: number) => {
    const packagePriceWithDiscount = calculatePackagePriceWithDiscount(packagePrice, discountPercentage);
    return packagePriceWithDiscount + extrasCost;
  };

  // Función para obtener el costo total de extras de un evento
  const getEventExtrasCost = (event: Event) => {
    // Priorizar total_extras_cost si existe y es un número válido
    if (event.total_extras_cost !== null && event.total_extras_cost !== undefined && !isNaN(Number(event.total_extras_cost))) {
      const totalExtras = Number(event.total_extras_cost);
      if (totalExtras > 0) {
        return totalExtras;
      }
    }
    
    // Si no hay total_extras_cost válido, calcular manualmente sumando todos los costos individuales
    const tablesCost = Number(event.tables_cost || 0);
    const chairsCost = Number(event.chairs_cost || 0);
    const playpensCost = Number(event.playpens_cost || 0);
    const toysCost = Number(event.toys_cost || 0);
    const servicesCost = Number(event.extra_services_cost || 0);
    
    const manualTotal = tablesCost + chairsCost + playpensCost + toysCost + servicesCost;
    
    // Si el cálculo manual da un resultado válido, usarlo
    if (manualTotal > 0) {
      return manualTotal;
    }
    
    // Como último recurso, usar extra_hours_cost si existe
    if (event.extra_hours_cost !== null && event.extra_hours_cost !== undefined && !isNaN(Number(event.extra_hours_cost))) {
      const hoursCost = Number(event.extra_hours_cost);
      if (hoursCost > 0) {
        return hoursCost;
      }
    }
    
    return 0;
  };

  const getEventStats = () => {
    const total = events.length;
    const pending = events.filter(e => e.status === EventStatus.PENDING).length;
    const confirmed = events.filter(e => e.status === EventStatus.CONFIRMED).length;
    const completed = events.filter(e => e.status === EventStatus.COMPLETED).length;
    const totalRevenue = events.reduce((sum, e) => {
      const eventPackage = packages.find(pkg => pkg.id === e.package_id);
      const packagePrice = eventPackage ? eventPackage.price : 0;
      const discountPercentage = e.discount_percentage || 0;
      const totalExtrasCost = getEventExtrasCost(e);
      
      // Calcular precio total considerando descuento
      const totalCost = calculateTotalEventPrice(packagePrice, discountPercentage, totalExtrasCost);
      
      // Debug temporal - más detallado
      console.log('Event Debug:', {
        eventId: e.id,
        contactName: e.contact_name,
        packagePrice,
        discountPercentage,
        totalExtrasCost,
        packagePriceWithDiscount: calculatePackagePriceWithDiscount(packagePrice, discountPercentage),
        totalCost,
        eventData: {
          total_extras_cost: e.total_extras_cost,
          extra_hours_cost: e.extra_hours_cost,
          extra_services_cost: e.extra_services_cost,
          advance_payment_amount: e.advance_payment_amount,
          advance_payment: e.advance_payment,
          // Todos los campos de extras
          extra_hours_count: e.extra_hours_count,
          extra_tables: e.extra_tables,
          extra_chairs: e.extra_chairs,
          extra_playpens: e.extra_playpens,
          extra_toys: e.extra_toys,
          tables_cost: e.tables_cost,
          chairs_cost: e.chairs_cost,
          playpens_cost: e.playpens_cost,
          toys_cost: e.toys_cost
        }
      });

      // Debug específico para eventos con precios sospechosos
      if (totalCost > packagePrice * 1.5) {
        console.warn('⚠️ PRECIO SOSPECHOSO DETECTADO:', {
          eventId: e.id,
          contactName: e.contact_name,
          packagePrice,
          totalCost,
          totalExtrasCost,
          packagePriceWithDiscount: calculatePackagePriceWithDiscount(packagePrice, discountPercentage),
          ratio: totalCost / packagePrice,
          eventData: e
        });
      }
      
      return sum + totalCost;
    }, 0);
    
    return { total, pending, confirmed, completed, totalRevenue };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const stats = getEventStats();

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
        Gestión de Eventos
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <EventIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Eventos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <Chip label="Pendientes" color="warning" sx={{ mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {stats.pending}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Eventos Pendientes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <Chip label="Confirmados" color="success" sx={{ mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {stats.confirmed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Eventos Confirmados
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <MoneyIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                ${formatPrice(stats.totalRevenue)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ingresos Totales
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Buscar por cliente, teléfono o ubicación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Filtrar por Estado</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as EventStatus | 'all')}
                label="Filtrar por Estado"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value={EventStatus.PENDING}>Pendientes</MenuItem>
                <MenuItem value={EventStatus.CONFIRMED}>Confirmados</MenuItem>
                <MenuItem value={EventStatus.IN_PROGRESS}>En Progreso</MenuItem>
                <MenuItem value={EventStatus.COMPLETED}>Completados</MenuItem>
                <MenuItem value={EventStatus.CANCELLED}>Cancelados</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => handleOpenDialog()}
              sx={{ height: '56px' }}
            >
              Nuevo Evento
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla de eventos */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>Contacto</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Horario</TableCell>
                <TableCell>Paquete</TableCell>
                <TableCell>Ubicación</TableCell>
                <TableCell>Precio</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEvents.map((event) => {
                const eventPackage = packages.find(pkg => pkg.id === event.package_id);
                return (
                  <TableRow key={event.id} hover>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {event.contact_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">{event.contact_phone}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">{event.secondary_phone || 'N/A'}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {(() => {
                          // Crear fecha local sin problemas de zona horaria
                          const [year, month, day] = event.event_date.split('T')[0].split('-');
                          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                          return date.toLocaleDateString('es-ES');
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTime(event.start_time)}
                        {event.extra_hours > 0 && ` (+${event.extra_hours}h extras)`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="primary">
                        {eventPackage?.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">{event.address}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          ${formatPrice(calculateTotalEventPrice(
                            eventPackage ? eventPackage.price : 0,
                            event.discount_percentage || 0,
                            getEventExtrasCost(event)
                          ))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Anticipo: ${formatPrice(event.advance_payment_amount || event.advance_payment || 0)}
                        </Typography>
                        {event.discount_percentage && event.discount_percentage > 0 && (
                          <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                            Descuento: {event.discount_percentage}%
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(event.status)}
                        color={getStatusColor(event.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(event)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(event.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog para crear/editar evento */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEvent ? 'Editar Evento' : 'Nuevo Evento'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Información de Contacto */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Información de Contacto
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre del Cliente"
                value={formData.contact_name}
                onChange={(e) => handleInputChange('contact_name', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono Principal"
                value={formData.contact_phone}
                onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono Secundario (Opcional)"
                value={formData.secondary_phone}
                onChange={(e) => handleInputChange('secondary_phone', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Ubicación */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Ubicación del Evento
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección Completa"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                multiline
                rows={2}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL de Google Maps (Opcional)"
                value={formData.google_maps_url}
                onChange={(e) => handleInputChange('google_maps_url', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                placeholder="https://maps.google.com/..."
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Fecha y Hora */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Fecha y Hora
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha del Evento"
                type="date"
                value={formData.event_date}
                onChange={(e) => handleInputChange('event_date', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Hora de Inicio"
                type="time"
                value={formData.start_time}
                onChange={(e) => handleInputChange('start_time', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Paquete y Horas Extras */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Paquete y Horas Extras
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mt: 2, mb: 1 }} required>
                <InputLabel>Paquete</InputLabel>
                <Select
                  value={formData.package_id}
                  onChange={(e) => handleInputChange('package_id', e.target.value)}
                  label="Paquete"
                >
                  {packages.map((pkg) => (
                    <MenuItem key={pkg.id} value={pkg.id}>
                      {pkg.name} - ${formatPrice(pkg.price)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Descuento sobre el Paquete (%)"
                type="number"
                value={formData.discount_percentage}
                onChange={(e) => handleInputChange('discount_percentage', parseFloat(e.target.value))}
                sx={{ mt: 2, mb: 1 }}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                helperText="Porcentaje de descuento sobre el precio del paquete (0-100%)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                <InputLabel>Horas Extras</InputLabel>
                <Select
                  value={formData.extra_hours}
                  onChange={(e) => handleInputChange('extra_hours', parseInt(e.target.value as string))}
                  label="Horas Extras"
                >
                  {Array.from({ length: 4 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {i === 0 ? 'Sin horas extras' : `${i} hora${i > 1 ? 's' : ''} extra${i > 1 ? 's' : ''}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Estado y Notas */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Estado y Notas
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  label="Estado"
                >
                  <MenuItem value={EventStatus.PENDING}>Pendiente</MenuItem>
                  <MenuItem value={EventStatus.CONFIRMED}>Confirmado</MenuItem>
                  <MenuItem value={EventStatus.IN_PROGRESS}>En Progreso</MenuItem>
                  <MenuItem value={EventStatus.COMPLETED}>Completado</MenuItem>
                  <MenuItem value={EventStatus.CANCELLED}>Cancelado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas Adicionales"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                placeholder="Información adicional sobre el evento..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            {editingEvent ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Events;
